import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "backdrop", "panel"]

  static values = {
    open: { type: Boolean, default: false },
    autoOpen: { type: Boolean, default: false },
    closeOnSubmit: { type: Boolean, default: false },
    resetFormOnClose: { type: String },
    transition: { type: Boolean, default: true }
  }

  connect() {
    this.id = this.element.id

    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundClose = this.close.bind(this)

    if (this.autoOpenValue) {
      if (this.transitionValue) {
        this.open()
      } else {
        // No transition needed, just mark as open
        this.openValue = true
      }
    }

    document.addEventListener("ui:dialog:open", this.handleOpenEvent)
    document.addEventListener("ui:dialog:close", this.handleCloseEvent)
    document.addEventListener("keydown", this.boundHandleKeydown)

    if (this.closeOnSubmitValue) {
      document.addEventListener("turbo:submit-start", this.boundClose)
    }
  }

  disconnect() {
    document.removeEventListener("ui:dialog:open", this.handleOpenEvent)
    document.removeEventListener("ui:dialog:close", this.handleCloseEvent)
    document.removeEventListener("keydown", this.boundHandleKeydown)

    if (this.closeOnSubmitValue) {
      document.removeEventListener("turbo:submit-start", this.boundClose)
    }
  }

  clickSafeZone() {
    console.error("clickSafeZone MUST BE DEFINED IN CHILD CONTROLLER")
  }

  clickAway(event) {
    // don't close if clicking on or within the panel
    const safeZone = this.clickSafeZone()

    if (!safeZone) return

    if (safeZone.contains(event.target)) return

    this.cancel()
  }

  cancel() {
    this.resetForm()
    this.close()
  }

  resetForm() {
    if (!this.resetFormOnCloseValue) return

    const form = this
      .dialogTarget
      .parentElement
      .querySelector(`#${this.resetFormOnCloseValue}`)

    if (form) form.reset()
  }

  open() {
    // if already open, skip
    if (this.openValue) return

    this.openValue = true

    this.showDialog()
    this.transitionBackdropIn()
    this.transitionPanelIn()
  }

  close() {
    // if already closed, skip
    if (!this.openValue) return

    this.openValue = false
    this.dispatch("closed")

    this.transitionBackdropOut()
    this.transitionPanelOut()
    this.hideDialog()
  }

  applyTransition(element, options) {
    // If transitions are disabled, directly apply final state
    if (!this.transitionValue) {
      if (this.openValue) {
        // Opening without transition
        if (options.enterFrom) {
          element.classList.remove(...options.enterFrom.split(" "))
        }
        if (options.enterTo) {
          element.classList.add(...options.enterTo.split(" "))
        }
      } else {
        // Closing without transition
        if (options.leaveFrom) {
          element.classList.remove(...options.leaveFrom.split(" "))
        }
        if (options.leaveTo) {
          element.classList.add(...options.leaveTo.split(" "))
        }
      }
      return
    }

    if (this.openValue) {

      // Apply enter transition
      if (options.enter) {
        element.classList.add(...options.enter.split(" "))
      }

      if (options.enterFrom) {
        element.classList.add(...options.enterFrom.split(" "))
      }

      // force browser reflow
      element.offsetWidth

      if (options.enterFrom && options.enterTo) {
        element.classList.remove(...options.enterFrom.split(" "))
        element.classList.add(...options.enterTo.split(" "))
      }
    } else {

      // Apply close transition
      if (options.leave) {
        element.classList.add(...options.leave.split(" "))
      }

      if (options.leaveFrom) {
        element.classList.add(...options.leaveFrom.split(" "))
      }

      element.offsetWidth

      if (options.leaveFrom && options.leaveTo) {
        element.classList.remove(...options.leaveFrom.split(" "))
        element.classList.add(...options.leaveTo.split(" "))
      }
    }
  }

  handleKeydown(event) {
    if (event.key == "Escape" && this.openValue) {
      this.close()
    }
  }

  handleOpenEvent = (event) => {
    if (event.detail.id == this.id) {
      this.open()
    }
  }

  handleCloseEvent = (event) => {
    if (event.detail.id == this.id) {
      this.close()
    }
  }

  showDialog() {
    this.dialogTarget.classList.remove("hidden")
  }

  transitionBackdropIn() {
    console.error("transitionBackdropIn MUST BE DEFINED IN CHILD CONTROLLER")
  }

  transitionBackdropOut() {
    console.error("transitionBackdropOut MUST BE DEFINED IN CHILD CONTROLLER")
  }

  transitionPanelIn() {
    console.error("transitionPanelIn MUST BE DEFINED IN CHILD CONTROLLER")
  }

  transitionPanelOut() {
    console.error("transitionPanelOut MUST BE DEFINED IN CHILD CONTROLLER")
  }

  hideDialog() {
    console.error("hideDialog MUST BE DEFINED IN CHILD CONTROLLER")
  }
}
